import {css, Typography, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  ConfirmDialog,
  DlMuiDynamicIcon,
  ExportUtils,
  GetStylesFunction,
  useApiQuery,
  useCurrentLocale,
} from '@wallaby-ui/core';
import {flattenTableParams, useFormatters} from '@wallaby-ui/document';
import {useCallback} from 'react';
import * as React from 'react';

import {getMedicationFilterData, PhRecPageFilters} from './PhRecommendationListLayout';

export function usePhRecommendationsExport(filters: PhRecPageFilters, config: ConfigObjectType, totalCount: number | undefined) {
  const currentLocale = useCurrentLocale();
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const {t} = useFormatters();

  const apiUrl = config.service.url.maxExportRows;

  const {data: maxExportedRows} = useApiQuery<number | undefined>([apiUrl], null, {
    axiosConfig: {
      url: apiUrl,
    },
    reactQueryOptions: {
      enabled: true,
    },
  });

  const downloadXlsxExport = useCallback(() => {
    ExportUtils.handleExportXlsx({
      url: config.service.url.recommendationsExport,
      filters: flattenTableParams({
        patientFullName: filters.patientFullName,
        patientIdentificationNumber: filters.patientIdentificationNumber,
        patientSrcId: filters.patientSrcId,
        encounterStartedFrom: filters.encounterStartedFrom,
        encounterStartedTo: filters.encounterStartedTo,
        pharmaRecommendationOrganizationIds:
          filters.pharmaRecommendationOrganizationIds && filters.pharmaRecommendationOrganizationIds.join(','),
        pharmaRecommendationDateFrom: filters.pharmaRecommendationDateFrom,
        pharmaRecommendationDateTo: filters.pharmaRecommendationDateTo,
        wasPublished: filters.wasPublished,
        riskiness: filters.riskiness,
        riskFactorTypes: filters.riskFactorTypes && filters.riskFactorTypes.join(','),
        summaryAndConclusionSearchString: filters.summaryAndConclusionSearchString,
        hasDrp: filters.hasDrp,
        followUpDateFrom: filters.followUpDateFrom,
        followUpDateTo: filters.followUpDateTo,
        procedures: filters.procedures && filters.procedures.join(','),
        drpFilter: {
          ...getMedicationFilterData(filters.drpFilter),
          types: filters.drpFilter?.types && filters.drpFilter.types.join(','),
          causes: filters.drpFilter?.causes && filters.drpFilter.causes.join(','),
          plannedInterventions: filters.drpFilter?.plannedInterventions && filters.drpFilter.plannedInterventions.join(','),
          manifestation: filters.drpFilter?.manifestation,
          interventionAcceptances: filters.drpFilter?.interventionAcceptances && filters.drpFilter.interventionAcceptances.join(','),
          interventionNotAcceptedReasons:
            filters.drpFilter?.interventionNotAcceptedReasons && filters.drpFilter.interventionNotAcceptedReasons.join(','),
          statuses: filters.drpFilter?.statuses && filters.drpFilter.statuses.join(','),
          statusAssessedOnFrom: filters.drpFilter?.statusAssessedOnFrom,
          statusAssessedOnTo: filters.drpFilter?.statusAssessedOnTo,
          notesSearchString: filters.drpFilter?.notesSearchString,
        },
      }),
      locale: currentLocale,
    });
  }, [
    config.service.url.recommendationsExport,
    currentLocale,
    filters.drpFilter,
    filters.encounterStartedFrom,
    filters.encounterStartedTo,
    filters.followUpDateFrom,
    filters.followUpDateTo,
    filters.hasDrp,
    filters.patientFullName,
    filters.patientIdentificationNumber,
    filters.patientSrcId,
    filters.pharmaRecommendationDateFrom,
    filters.pharmaRecommendationDateTo,
    filters.pharmaRecommendationOrganizationIds,
    filters.procedures,
    filters.riskFactorTypes,
    filters.riskiness,
    filters.summaryAndConclusionSearchString,
    filters.wasPublished,
  ]);

  const [confirmExportWarningOpened, setConfirmExportWarningOpened] = React.useState(false);
  const onConfirmExportWarning = useCallback(() => {
    setConfirmExportWarningOpened(false);
    downloadXlsxExport();
  }, [downloadXlsxExport]);

  const isWarningDialogVisible = useCallback(() => {
    if (!maxExportedRows) return;
    return totalCount != null && totalCount > maxExportedRows;
  }, [totalCount, maxExportedRows]);

  const handleXlsxExport = useCallback(() => {
    if (isWarningDialogVisible()) {
      setConfirmExportWarningOpened(true);
    } else {
      downloadXlsxExport();
    }
  }, [isWarningDialogVisible, downloadXlsxExport]);

  const handleCancel = useCallback(() => {
    // noop
  }, []);

  const warningDialog = isWarningDialogVisible() ? (
    <ConfirmDialog
      isOpened={confirmExportWarningOpened}
      handleConfirm={onConfirmExportWarning}
      handleCancel={handleCancel}
      showCancel={false}
      titleIcon={
        <DlMuiDynamicIcon
          iconName='haidiWarningIcon'
          useFixedViewBox='0 0 13 12'
          css={muiStyles.confirmIconButton}
          svgIconInputCss={muiStyles.confirmIcon}
          noButtonStyles
        />
      }
      titleText={t('common.export.confirmDialogLimit.title')}
      confirmText={t('common.confirmDialog.accept')}
    >
      <Typography variant='value'>{t('common.export.confirmDialogLimit.text')}</Typography>
    </ConfirmDialog>
  ) : undefined;

  return {handleXlsxExport, warningDialog};
}

const getStyles: GetStylesFunction = (options) => ({
  confirmIconButton: css`
    color: var(--dlTheme-500);
    padding: 0;
  `,
  confirmIcon: css`
    font-size: 20px;
  `,
});
